import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useProdAuth } from '../../contexts/ProdAuthContext';
import { ProductionLogin } from './ProductionLogin';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const ACCENT = '#4A7C59';

const ROLE_BADGES: Record<string, string> = {
  field: 'bg-blue-100 text-blue-700',
  transport: 'bg-amber-100 text-amber-700',
  administration: 'bg-purple-100 text-purple-700',
  admin: 'bg-red-100 text-red-700',
  super_admin: 'bg-green-100 text-green-700',
};

const ROLE_LABELS: Record<string, string> = {
  field: 'Field',
  transport: 'Transport',
  administration: 'Administration',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  roles?: string[]; // undefined = all roles
  section?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/production', label: 'Dashboard', end: true },
  // Field — station management
  { to: '/production/deliveries', label: 'Cherry Deliveries' },
  { to: '/production/farmers', label: 'Farmers', roles: ['field', 'admin', 'super_admin'], section: 'Field' },
  { to: '/production/centres', label: 'Collection Centres', roles: ['field', 'admin', 'super_admin'] },
  // Transport — trips & logistics
  { to: '/production/trips', label: 'Trips', roles: ['transport', 'admin', 'super_admin'], section: 'Transport' },
  // Administration — processing pipeline
  { to: '/production/batches', label: 'Batches', roles: ['administration', 'admin', 'super_admin'], section: 'Administration' },
  { to: '/production/drying', label: 'Drying Beds', roles: ['administration', 'admin', 'super_admin'] },
  { to: '/production/inventory', label: 'Inventory', roles: ['administration', 'admin', 'super_admin'] },
  { to: '/production/reports', label: 'Reports', roles: ['administration', 'admin', 'super_admin'] },
  // Admin
  { to: '/production/hub-staff', label: 'Hub Staff', roles: ['administration', 'admin', 'super_admin'], section: 'Admin' },
  { to: '/production/users', label: 'System Accounts', roles: ['admin', 'super_admin'] },
];

export const ProductionLayout: React.FC = () => {
  const { user, loading, logout } = useProdAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!user) return <ProductionLogin />;

  const handleLogout = async () => {
    await logout();
    navigate('/production/login');
  };

  const visibleItems = NAV_ITEMS.filter(
    item => !item.roles || item.roles.includes(user.role)
  );

  return (
    <div className="admin-shell min-h-screen bg-[#FAFAF8] flex">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-[#E8E6E2] flex flex-col">
        <div className="p-6 border-b border-[#E8E6E2]">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: ACCENT }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <h1 className="text-sm font-bold text-[#1A1A1A]">Lot 20</h1>
          </div>
          <p className="text-[9px] text-[#A3A3A3] uppercase tracking-[0.2em] font-medium">Production</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleItems.map((item, i) => {
            const prevItem = visibleItems[i - 1];
            const showSection = item.section && item.section !== prevItem?.section;
            return (
              <React.Fragment key={item.to}>
                {showSection && (
                  <p className="text-[9px] text-[#A3A3A3] uppercase tracking-[0.2em] font-medium px-3 pt-4 pb-1">
                    {item.section}
                  </p>
                )}
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'text-white'
                        : 'text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8]'
                    }`
                  }
                  style={({ isActive }) => isActive ? { backgroundColor: ACCENT } : {}}
                >
                  {item.label}
                </NavLink>
              </React.Fragment>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E8E6E2]">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs font-medium text-[#1A1A1A] truncate">{user.name}</p>
            <span className={`inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${ROLE_BADGES[user.role] || 'bg-neutral-100 text-neutral-600'}`}>
              {ROLE_LABELS[user.role] ?? user.role.replace('_', ' ')}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-3 py-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors text-left"
          >
            Sign out
          </button>
          <a
            href="/admin"
            className="block px-3 py-2 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors"
          >
            Admin panel
          </a>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};
